export interface ShowcaseProject {
  slug: string;
  title: string;
  status: 'concept' | 'in-development' | 'beta' | 'live';
  summary: string;
  problem: string;
  techStack: string[];
  repoUrl?: string;
  demoUrl?: string;
  impactGoal: string;
  features: string[];
}

export const showcaseProjects: ShowcaseProject[] = [
  {
    slug: 'learning-platform',
    title: 'Second Story Learning Platform',
    status: 'live',
    summary:
      'A full-stack education platform with AI-powered mentoring, personalized learning paths, and real-time progress tracking — built specifically for justice-impacted learners.',
    problem:
      'Traditional coding bootcamps are expensive, inflexible, and rarely account for the unique barriers justice-impacted individuals face re-entering the workforce. There is no affordable, adaptive platform that combines technical training with narrative development.',
    techStack: [
      'React',
      'TypeScript',
      'Vite',
      'Express',
      'Supabase',
      'Claude AI',
      'TailwindCSS',
      'Redis',
    ],
    demoUrl: 'https://www.straydog-secondstory.org',
    impactGoal:
      'Provide free, AI-augmented technical education to 50+ justice-impacted individuals in the first year of operation.',
    features: [
      'AI-powered code review and mentoring via Claude',
      'Structured learning paths from beginner to job-ready',
      'GitHub integration for portfolio building',
      'Mentor matching and session scheduling',
      'Real-time analytics and progress dashboards',
      'Accessibility-first design (WCAG AA)',
    ],
  },
  {
    slug: 'ai-orchestrator',
    title: 'AI Orchestrator',
    status: 'in-development',
    summary:
      'An intelligent workflow engine that coordinates multiple AI models and human mentors to deliver adaptive, context-aware learning experiences.',
    problem:
      'Single-model AI tutoring is brittle — it cannot gauge learner frustration, adapt pacing across topics, or know when to escalate to a human mentor. Effective learning requires orchestration across multiple intelligence sources.',
    techStack: [
      'TypeScript',
      'Node.js',
      'Claude API',
      'LangChain',
      'Redis',
      'PostgreSQL',
    ],
    impactGoal:
      'Reduce learner dropout rates by 40% through intelligent intervention timing and adaptive difficulty scaling.',
    features: [
      'Multi-model routing for specialized learning tasks',
      'Context-aware escalation to human mentors',
      'Adaptive difficulty based on learner performance',
      'Session memory and long-term progress tracking',
      'Automated curriculum gap detection',
    ],
  },
  {
    slug: 'slack-mentorship-ecosystem',
    title: 'Slack Mentorship Ecosystem',
    status: 'beta',
    summary:
      'A Slack-integrated bot and channel system that brings daily standups, peer code help, job postings, and AI-assisted mentoring directly into learners\' existing communication tools.',
    problem:
      'Justice-impacted learners often lack professional networks and daily accountability structures. Email-based programs have low engagement. Meeting learners where they already communicate — in Slack — dramatically improves retention.',
    techStack: [
      'Slack Bolt',
      'TypeScript',
      'Node.js',
      'Claude AI',
      'Supabase',
      'n8n',
    ],
    impactGoal:
      'Achieve 70%+ daily active engagement among enrolled learners through low-friction communication channels.',
    features: [
      'Daily standup prompts and streak tracking',
      'AI-powered /codehelp slash command',
      'Curated job board with real-time postings',
      'Peer recognition and wins celebrations',
      'Automated content aggregation from tech news sources',
      'Admin monitoring and community health analytics',
    ],
  },
  {
    slug: 'reentry-resource-navigator',
    title: 'Reentry Resource Navigator',
    status: 'concept',
    summary:
      'A location-aware tool that helps justice-impacted individuals find reentry services, legal aid, housing assistance, and employment programs in their area.',
    problem:
      'Reentry resources are fragmented across hundreds of local nonprofits, government agencies, and community organizations. No single platform aggregates these resources in a user-friendly, location-aware format.',
    techStack: [
      'React',
      'TypeScript',
      'Mapbox',
      'Supabase',
      'Node.js',
    ],
    impactGoal:
      'Connect individuals with relevant reentry services within 48 hours of release, reducing recidivism-contributing resource gaps.',
    features: [
      'Geolocation-based resource discovery',
      'Filterable by service type (housing, legal, employment, health)',
      'Verified provider listings with community reviews',
      'Offline-capable for facility use',
      'Referral tracking for impact measurement',
    ],
  },
];

export const getProjectBySlug = (slug: string): ShowcaseProject | undefined =>
  showcaseProjects.find((p) => p.slug === slug);

export const getStatusLabel = (status: ShowcaseProject['status']): string => {
  switch (status) {
    case 'concept':
      return 'Concept';
    case 'in-development':
      return 'In Development';
    case 'beta':
      return 'Beta';
    case 'live':
      return 'Live';
  }
};

export const getStatusColor = (status: ShowcaseProject['status']): string => {
  switch (status) {
    case 'concept':
      return 'bg-secondary-100 text-secondary-700 border-secondary-200';
    case 'in-development':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'beta':
      return 'bg-accent-50 text-accent-700 border-accent-200';
    case 'live':
      return 'bg-success-50 text-success-700 border-success-200';
  }
};
